import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Renderer, Program, Mesh, Triangle } from "ogl";

import { cn } from "../../lib/utils.js";

/**
 * Secondary / navigation button (Prev · Next · Back …) with a WebGL specular
 * highlight that travels its border toward the pointer -- recreated from
 * React Bits' Specular Button. The CSS border always stays, so the button
 * looks and works normally if WebGL is unavailable.
 */
const specularButtonVariants = cva(
  "specular-button relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap border border-surface-200 font-medium leading-none tracking-[0.01em] text-surface-700 transition-[transform,background-color] duration-150 active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "h-7 gap-1 px-2.5 text-xs",
        md: "h-8 gap-1.5 px-3 text-[13px]",
        lg: "h-10 gap-2 px-5 text-sm",
      },
      /** The WebGL border reads the resulting radius, so it always matches. */
      radius: {
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        full: "rounded-full",
      },
      surface: {
        /** Matches the app's existing secondary buttons. */
        solid: "bg-surface-0 hover:bg-surface-50",
        ghost: "bg-transparent hover:bg-surface-100",
        glass: "bg-surface-0/50 backdrop-blur-sm hover:bg-surface-0/70",
      },
    },
    defaultVariants: { size: "md", radius: "md", surface: "solid" },
  }
);

/** Every default SpecularButton falls back to -- pass any at the call site to change it. */
export const specularButtonDefaults = {
  size: "md",
  radius: "md",
  surface: "solid",
  /** Highlight color. Default: the theme's --sb-line (steel blue light / white dark). */
  lineColor: undefined as string | undefined,
  /** Extra shader-drawn stroke under the highlight. Default: none (the CSS border is the base). */
  baseColor: undefined as string | undefined,
  intensity: 0.5,
  /** Degrees of border that are fully lit around the light direction. */
  shineSize: 10,
  /** Degrees over which the highlight fades out past shineSize. */
  shineFade: 40,
  /** Highlight line thickness, px. */
  thickness: 1,
  /** "sharp": crisp line exactly `thickness` px wide. "soft": blurred glow that fades out over a few px. */
  lineStyle: "sharp" as "sharp" | "soft",
  /** Radians per second for autoAnimate. */
  speed: 0.35,
  followMouse: true,
  /** px from the button within which the highlight starts to appear. */
  proximity: 250,
  autoAnimate: false,
} as const;

export interface SpecularButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof specularButtonVariants> {
  lineColor?: string;
  baseColor?: string;
  intensity?: number;
  shineSize?: number;
  shineFade?: number;
  thickness?: number;
  speed?: number;
  followMouse?: boolean;
  lineStyle?: "sharp" | "soft";
  proximity?: number;
  /** Keep the highlight lit and circling the border (ignored under reduced motion). */
  autoAnimate?: boolean;
}

interface LiveProps {
  lineColor: string | undefined;
  baseColor: string | undefined;
  intensity: number;
  shineSize: number;
  shineFade: number;
  thickness: number;
  speed: number;
  followMouse: boolean;
  lineStyle: "sharp" | "soft";
  proximity: number;
  autoAnimate: boolean;
  disabled: boolean;
}

// Canvas bleeds past the button on every side so the rim glow isn't clipped.
const PAD = 20;

const VERT = `#version 300 es
in vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }
`;

const FRAG = `#version 300 es
precision highp float;

uniform vec2 uCenter;
uniform vec2 uHalfSize;
uniform float uRadius;
uniform float uAngle;
uniform float uPx;
uniform vec3 uLineColor;
uniform vec3 uBaseColor;
uniform float uBaseStrength;
uniform float uIntensity;
uniform float uShineSize;
uniform float uShineFade;
uniform float uThickness;
uniform float uBaseWidth;
uniform float uSharp;

out vec4 fragColor;

float sdRoundedRect(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float gaussianLine(float d, float sigma) {
  float x = d / (sigma + 1e-6);
  float k = mix(1.0, 1.6, smoothstep(0.0, 1.5, x));
  return exp(-k * x * x);
}

void main() {
  vec2 p = gl_FragCoord.xy - uCenter;
  float d = sdRoundedRect(p, uHalfSize, uRadius);
  vec2 L = vec2(cos(uAngle), sin(uAngle));

  // Optional dark base stroke hugging the edge.
  float base = (1.0 - smoothstep(0.0, uBaseWidth, abs(d))) * 0.45 * uBaseStrength;

  // Symmetric specular: the edges facing toward/away from the light both
  // catch a streak. The angular window is measured with an elliptical normal
  // so it varies continuously along straight edges.
  vec2 nEll = normalize(p / (uHalfSize * uHalfSize) + 1e-6);
  float phi = acos(clamp(abs(dot(nEll, L)), 0.0, 1.0));
  float rim = 1.0 - smoothstep(uShineSize - uShineFade, uShineSize + uShineFade + 1e-4, phi);
  // Sharp: a solid band exactly uThickness wide with only a half-pixel
  // anti-aliased edge, so the line stays crisp as it travels. Soft: the
  // original Gaussian glow, capped a few pixels out.
  float crisp = 1.0 - smoothstep(0.5 * uThickness - 0.5 * uPx, 0.5 * uThickness + 0.5 * uPx, abs(d));
  float soft = gaussianLine(d, uThickness) * (1.0 - smoothstep(0.5 * uPx, 3.0 * uPx, abs(d)));
  float line = mix(soft, crisp, uSharp);
  float hi = line * rim * uIntensity;

  vec3 col = uBaseColor * base + uLineColor * hi;
  float a = clamp(base + hi, 0.0, 1.0);
  fragColor = vec4(col, a);
}
`;

// Any CSS color (hex, rgb(), named, color-mix result) -> linear [0..1] RGB,
// via the 2D canvas's own color parser.
let colorCtx: CanvasRenderingContext2D | null = null;
function cssColorToRgb(color: string): [number, number, number] | null {
  colorCtx ??= document.createElement("canvas").getContext("2d");
  if (!colorCtx || !color) return null;
  colorCtx.fillStyle = "#000";
  colorCtx.fillStyle = color;
  const v = String(colorCtx.fillStyle);
  if (v.startsWith("#")) {
    const n = parseInt(v.slice(1, 7), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  const m = v.match(/[\d.]+/g);
  return m && m.length >= 3 ? [Number(m[0]) / 255, Number(m[1]) / 255, Number(m[2]) / 255] : null;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SpecularButton = React.forwardRef<HTMLButtonElement, SpecularButtonProps>(
  (
    {
      className, size, radius, surface, children, type = "button", disabled = false,
      lineColor = specularButtonDefaults.lineColor,
      baseColor = specularButtonDefaults.baseColor,
      intensity = specularButtonDefaults.intensity,
      shineSize = specularButtonDefaults.shineSize,
      shineFade = specularButtonDefaults.shineFade,
      thickness = specularButtonDefaults.thickness,
      speed = specularButtonDefaults.speed,
      followMouse = specularButtonDefaults.followMouse,
      lineStyle = specularButtonDefaults.lineStyle,
      proximity = specularButtonDefaults.proximity,
      autoAnimate = specularButtonDefaults.autoAnimate,
      ...props
    },
    ref
  ) => {
    const btnRef = React.useRef<HTMLButtonElement | null>(null);
    const fxRef = React.useRef<HTMLSpanElement>(null);
    const wakeRef = React.useRef<() => void>(() => {});
    const propsRef = React.useRef<LiveProps>({} as LiveProps);
    // Read every frame by the render loop, so prop changes apply without
    // tearing down the WebGL context.
    propsRef.current = { lineColor, baseColor, intensity, shineSize, shineFade, thickness, speed, followMouse, lineStyle, proximity, autoAnimate, disabled };

    // Creating the GL context and compiling the shader costs real main-thread time,
    // and a screen with several of these pays it all in the frame that mounts them --
    // enough to be felt as a stall when switching to a dense tab. The button is dark
    // and idle at rest anyway (autoAnimate defaults false), so the effect layer is
    // built once the browser is idle rather than during the mount itself. Until then
    // the plain CSS button is already on screen and fully usable.
    const [fxReady, setFxReady] = React.useState(false);
    React.useEffect(() => {
      const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
        cancelIdleCallback?: (h: number) => void;
      };
      if (w.requestIdleCallback) {
        const h = w.requestIdleCallback(() => setFxReady(true), { timeout: 400 });
        return () => w.cancelIdleCallback?.(h);
      }
      const t = setTimeout(() => setFxReady(true), 0);
      return () => clearTimeout(t);
    }, []);

    React.useEffect(() => {
      if (!fxReady) return;
      const btn = btnRef.current;
      const fx = fxRef.current;
      if (!btn || !fx) return;

      const dpr = window.devicePixelRatio || 1;
      let renderer: Renderer;
      try {
        renderer = new Renderer({ alpha: true, premultipliedAlpha: true, antialias: true, dpr });
      } catch {
        return; // No WebGL -- the plain CSS button still works.
      }
      const gl = renderer.gl;
      if (!gl || !renderer.isWebgl2) return; // the shader is GLSL 3.00 ES
      gl.clearColor(0, 0, 0, 0);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

      const geometry = new Triangle(gl);
      if (geometry.attributes.uv) delete geometry.attributes.uv;
      const program = new Program(gl, {
        vertex: VERT,
        fragment: FRAG,
        transparent: true,
        uniforms: {
          uCenter: { value: [0, 0] },
          uHalfSize: { value: [1, 1] },
          uRadius: { value: 0 },
          uAngle: { value: 2.4 },
          uPx: { value: dpr },
          uLineColor: { value: [1, 1, 1] },
          uBaseColor: { value: [0.32, 0.32, 0.32] },
          uBaseStrength: { value: 0 },
          uIntensity: { value: 0 },
          uShineSize: { value: 0.17 },
          uShineFade: { value: 0.7 },
          uThickness: { value: dpr },
          uBaseWidth: { value: dpr },
          uSharp: { value: 1 },
        },
      });
      const mesh = new Mesh(gl, { geometry, program });
      gl.canvas.setAttribute("aria-hidden", "true");
      fx.appendChild(gl.canvas);

      // Theme-driven highlight color (--sb-line), re-read on theme change.
      const colors = { themeLine: [1, 1, 1] as [number, number, number], radiusPx: 0, borderPx: 0 };
      const readTheme = () => {
        const cs = getComputedStyle(btn);
        colors.themeLine = cssColorToRgb(cs.getPropertyValue("--sb-line").trim()) ?? [1, 1, 1];
        colors.radiusPx = parseFloat(cs.borderTopLeftRadius) || 0;
        colors.borderPx = parseFloat(cs.borderTopWidth) || 0;
      };

      const size = { w: 1, h: 1 };
      const resize = () => {
        readTheme();
        // Fractional size + explicit center keep the SDF pinned to the exact
        // CSS border instead of drifting up to a pixel from offsetWidth rounding.
        const rect = btn.getBoundingClientRect();
        // The CSS border is painted inside the box edge, so inset the shape by
        // half the border width: the highlight's center line then runs down
        // the middle of the border itself instead of along its outer edge.
        const inset = colors.borderPx / 2;
        size.w = rect.width - inset * 2;
        size.h = rect.height - inset * 2;
        renderer.setSize(rect.width + PAD * 2, rect.height + PAD * 2);
        program.uniforms.uCenter.value = [(PAD + rect.width / 2) * dpr, (PAD + rect.height / 2) * dpr];
        program.uniforms.uHalfSize.value = [(size.w / 2) * dpr, (size.h / 2) * dpr];
      };

      // Light angle steers toward the pointer anywhere on the page; brightness
      // follows how close the pointer is.
      let pointerAngle: number | null = null;
      let proximityT = 0;
      let angle = 2.4;
      let idleAngle = 2.4;
      let bright = 0;
      let last = performance.now();
      let raf = 0;

      const update = (now: number) => {
        raf = 0;
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        const p = propsRef.current;
        const animate = p.autoAnimate && !prefersReducedMotion();

        idleAngle += p.speed * dt;
        const target = p.followMouse && pointerAngle != null && (!animate || proximityT > 0) ? pointerAngle : idleAngle;
        const diff = ((target - angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        angle += diff * (1 - Math.exp(-dt * 7));

        const brightTarget = p.disabled ? 0 : animate ? 1 : proximityT;
        bright += (brightTarget - bright) * (1 - Math.exp(-dt * 8));

        const line = (p.lineColor && cssColorToRgb(p.lineColor)) || colors.themeLine;
        const baseRgb = p.baseColor ? cssColorToRgb(p.baseColor) : null;
        program.uniforms.uAngle.value = angle;
        // Same half-border inset as the shape, so the corners follow the border's curve.
        const radiusPx = Math.max(0, colors.radiusPx - colors.borderPx / 2);
        program.uniforms.uRadius.value = Math.min(radiusPx, Math.min(size.w, size.h) / 2) * dpr;
        program.uniforms.uLineColor.value = line;
        program.uniforms.uBaseColor.value = baseRgb ?? [0, 0, 0];
        program.uniforms.uBaseStrength.value = baseRgb ? 1 : 0;
        program.uniforms.uIntensity.value = p.intensity * bright;
        program.uniforms.uShineSize.value = (p.shineSize * Math.PI) / 180;
        program.uniforms.uShineFade.value = (p.shineFade * Math.PI) / 180;
        program.uniforms.uThickness.value = p.thickness * dpr;
        program.uniforms.uSharp.value = p.lineStyle === "soft" ? 0 : 1;
        renderer.render({ scene: mesh });

        // Idle and dark: stop the loop until the pointer comes near again, so
        // buttons at rest cost nothing per frame.
        const settled = !animate && Math.abs(brightTarget - bright) < 0.002 && bright < 0.002;
        if (!settled) raf = requestAnimationFrame(update);
      };

      const wake = () => {
        if (raf) return;
        last = performance.now();
        raf = requestAnimationFrame(update);
      };
      wakeRef.current = wake;

      const onPointerMove = (e: PointerEvent) => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = Math.max(rect.left - e.clientX, 0, e.clientX - rect.right);
        const dy = Math.max(rect.top - e.clientY, 0, e.clientY - rect.bottom);
        const dist = Math.hypot(dx, dy);
        if (dist === 0) {
          // Over the button the light settles on the diagonal (framing the
          // corners) and gently sways with the cursor position within it.
          const nx = (e.clientX - cx) / (rect.width / 2);
          const ny = (cy - e.clientY) / (rect.height / 2);
          pointerAngle = Math.atan2(2 / rect.height, -2 / rect.width) + nx * 0.3 + ny * 0.15;
        } else {
          pointerAngle = Math.atan2(cy - e.clientY, e.clientX - cx);
        }
        const t = Math.max(0, 1 - dist / Math.max(propsRef.current.proximity, 1));
        proximityT = t * t * (3 - 2 * t);
        if (proximityT > 0 || bright > 0.002) wake();
      };

      const ro = new ResizeObserver(() => { resize(); wake(); });
      ro.observe(btn);
      resize();
      window.addEventListener("pointermove", onPointerMove, { passive: true });

      const themeObserver = new MutationObserver(() => { readTheme(); wake(); });
      themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
      const scheme = window.matchMedia("(prefers-color-scheme: dark)");
      const onScheme = () => { readTheme(); wake(); };
      scheme.addEventListener("change", onScheme);

      wake();

      return () => {
        cancelAnimationFrame(raf);
        wakeRef.current = () => {};
        ro.disconnect();
        themeObserver.disconnect();
        scheme.removeEventListener("change", onScheme);
        window.removeEventListener("pointermove", onPointerMove);
        if (gl.canvas.parentNode === fx) fx.removeChild(gl.canvas);
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      };
    }, [fxReady]);

    // Props that can light the button without a pointer move restart the loop.
    React.useEffect(() => { wakeRef.current(); }, [autoAnimate, disabled, intensity, lineColor, baseColor, thickness, lineStyle, shineSize, shineFade]);

    // Stable callback: wrappers such as Radix's `asChild` Slot pass a freshly
    // composed ref every render, and re-attaching it each render (null, then the
    // node) re-runs their state setters in an endless update loop.
    const forwardedRef = React.useRef(ref);
    forwardedRef.current = ref;
    const setRefs = React.useCallback((node: HTMLButtonElement | null) => {
      btnRef.current = node;
      const r = forwardedRef.current;
      if (typeof r === "function") r(node);
      else if (r) (r as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    }, []);

    return (
      <button
        ref={setRefs}
        type={type}
        disabled={disabled}
        className={cn(specularButtonVariants({ size, radius, surface }), className)}
        {...props}
      >
        <span ref={fxRef} aria-hidden="true" className="pointer-events-none absolute -inset-5 z-[1] [&>canvas]:block [&>canvas]:size-full" />
        <span className="relative z-[2] inline-flex items-center gap-[inherit]">{children}</span>
      </button>
    );
  }
);
SpecularButton.displayName = "SpecularButton";

export { SpecularButton, specularButtonVariants };
